import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import PublicOffersService, { PublicOffer } from '~/services/PublicOfferService';
import SearchService, { HotelSearchList } from '~/services/SearchService';

interface UseHotelOffersByPlaceIdsParams {
  placeIds: Array<string>;
  enabled?: boolean;
}

const STALE_TIME = 5 * 60 * 1000;
const region = 'AU';

export interface HotelOffer {
  id: string;
  title: string;
  imageId?: string;
  type: PublicOffer['type'];
  propertyId?: string;
}

function getOfferById(offerId: string, brand: App.Tenant['brand']): Promise<HotelOffer | null> {
  return PublicOffersService.getPublicOfferById(offerId, region, brand).then((response) => {
    const offer = response.result;
    const imageId = 'images' in offer && offer.images?.[0]?.id ? offer.images[0].id : undefined;

    return {
      id: offerId,
      title: offer.name || offerId,
      imageId,
      type: offer.type,
      propertyId: 'property' in offer ? offer.property.id : undefined,
    };
  });
}

export function useHotelOffersByPlaceIds({ placeIds, enabled = true }: UseHotelOffersByPlaceIdsParams) {
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const placeOffersQueries = useQueries({
    queries: placeIds.map((placeId) => ({
      queryKey: ['hotel-offers', 'by-place-id', placeId, brand, region],
      queryFn: async (): Promise<HotelSearchList> => {
        const result = await SearchService.getSearchLightOffers({
          region,
          brand: brand.toLowerCase(),
          destinationId: placeId,
          limit: 1000,
          noCache: true,
        });
        return result;
      },
      enabled: enabled && !!placeId,
      staleTime: STALE_TIME,
    })),
  });

  const allOfferIds = useMemo(() => {
    const offerIdSet = new Set<string>();
    placeOffersQueries.forEach((query) => {
      if (query.data) {
        query.data.forEach((offer) => {
          offerIdSet.add(offer.id);
        });
      }
    });
    return Array.from(offerIdSet);
  }, [placeOffersQueries]);

  const offerIdsByPlaceId = useMemo(() => {
    const mapping: Record<string, Array<string>> = {};
    placeIds.forEach((placeId, index) => {
      const query = placeOffersQueries[index];
      if (query?.data) {
        mapping[placeId] = query.data.map((offer) => offer.id);
      } else {
        mapping[placeId] = [];
      }
    });
    return mapping;
  }, [placeIds, placeOffersQueries]);

  const offerDetailsQueries = useQueries({
    queries: allOfferIds.map((offerId) => ({
      queryKey: ['hotel-offer-by-id', offerId, brand],
      queryFn: async (): Promise<HotelOffer | null> => {
        try {
          return await getOfferById(offerId, brand);
        } catch (error) {
          enqueueSnackbar(`Error fetching offer details for ${offerId}: ${error.message}`, { variant: 'error' });
          return null;
        }
      },
      enabled: enabled && !!offerId,
      staleTime: STALE_TIME,
    })),
  });

  const offers = useMemo(() => {
    return offerDetailsQueries
      .map((query) => query.data)
      .filter((offer): offer is HotelOffer => offer !== null && offer !== undefined);
  }, [offerDetailsQueries]);

  const isLoading =
    placeOffersQueries.some((query) => query.isLoading) || offerDetailsQueries.some((query) => query.isLoading);
  const isError =
    placeOffersQueries.some((query) => query.isError) || offerDetailsQueries.some((query) => query.isError);

  return {
    offers,
    isLoading,
    isError,
    allOfferIds,
    offerIdsByPlaceId,
  };
}
